import { File06, Folder, SearchMd } from '@cvpartner/design-system/icons/line';
import { mediaQuery } from '@cvpartner/design-system/vars';
import { useFlag } from '@unleash/proxy-client-react';
import type * as React from 'react';
import { useLocation } from 'react-router';
import { useMediaLayout } from 'use-media';
import { useLoggedInUser } from 'stores/CurrentUser';
import { usePrivacyNotice } from 'stores/PrivacyNotice';
import { I18n } from 'util/translations';
import { AdminNav } from './AdminNav';
import { NavItem } from './NavItem';
import { myCvButton, myCvIcon, myCvLink, nav } from './Navigation.css';
import { UserNav } from './UserNav';

const MyCvButton: React.FC<{ myCvUrl: string; showLinkText: boolean }> = ({
  myCvUrl,
  showLinkText,
}) => (
  <a href={myCvUrl} className={myCvLink}>
    <div className={myCvButton({ iconOnly: !showLinkText })}>
      <File06 className={myCvIcon} />
      {showLinkText ? I18n.t('My_CV') : null}
    </div>
  </a>
);

const isDashboard = window.location.pathname === '/dashboard';

export const Navigation: React.FC = () => {
  const showLinkText = useMediaLayout(mediaQuery.s);
  const { pathname } = useLocation();
  const showProposalsTab = useFlag('proposals-dedicated-tab');
  const privacyNotice = usePrivacyNotice();
  const {
    id,
    default_cv_id,
    isLimitedAccessUser,
    isConsultant,
    isCountrymanager,
    isReferenceManager,
    isInternationalmanager,
  } = useLoggedInUser();

  if (privacyNotice == null) {
    return null;
  }

  const mustAgreeToPrivacyNotice =
    privacyNotice.enabled && privacyNotice.mustAgree;

  if (
    mustAgreeToPrivacyNotice ||
    (!isLimitedAccessUser &&
      !isConsultant &&
      !isCountrymanager &&
      !isReferenceManager &&
      !isInternationalmanager)
  ) {
    return (
      <nav>
        <UserNav />
      </nav>
    );
  }

  const dashboardUrl = '/dashboard#/persons/proposal/false';
  let myCvUrl = dashboardUrl;

  if (id && default_cv_id) {
    myCvUrl += `/cv/${id}/${default_cv_id}`;
  }

  return (
    <nav className={nav}>
      <NavItem
        href={dashboardUrl}
        icon={SearchMd}
        linkText={
          showLinkText
            ? I18n.t('application_navigation.company_overview')
            : null
        }
        active={
          isDashboard &&
          (pathname.startsWith('/persons') ||
            pathname.startsWith('/cvs') ||
            pathname.startsWith('/references') ||
            pathname.startsWith('/customers'))
        }
      />
      {showProposalsTab ? (
        <NavItem
          href="/dashboard#/proposal"
          icon={Folder}
          linkText={showLinkText ? I18n.t('Offers') : null}
          active={isDashboard && pathname.startsWith('/proposal')}
        />
      ) : null}
      <MyCvButton myCvUrl={myCvUrl} showLinkText={showLinkText} />
      <UserNav />
      {isInternationalmanager || isCountrymanager ? (
        <AdminNav showLinkText={showLinkText} />
      ) : null}
    </nav>
  );
};
